'use client';
import type { ChangeEvent, ComponentProps, FocusEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { clsxm } from '@vs-shop/utils';

/**
 * One end of a range, typed rather than dragged.
 *
 * The native spinner arrows are hidden — the box is one of a pair inside a
 * narrow rail, and two sets of arrows there read as chrome rather than as
 * controls. The trailing padding is the room the unit suffix beside it hangs
 * in, so a value never runs under its own unit.
 */
const filterRangeInputClasses = [
  'arrows-hidden w-full rounded-md px-4 py-2 pr-10 text-right text-xs font-semibold',
  'bg-background border-foreground/20 border transition-colors',
  'hover:border-foreground/40 hover:outline-none',
  'focus-visible:border-foreground/60 focus-visible:outline-none',
];

export type FilterRangeInputProps = Omit<
  ComponentProps<'input'>,
  'value' | 'defaultValue' | 'onChange'
> & {
  /**
   * The committed end of the range, already formatted for reading. The caller
   * formats it because how many decimals a value carries follows from the
   * facet's step, which is the caller's arithmetic.
   */
  readonly value: string;
  /**
   * What was typed, once it has settled or the box has been left. Raw: the
   * caller parses, rounds to its step and clamps to its bounds, and is free to
   * reject the string entirely.
   */
  readonly onValueCommit: (value: string) => void;
  /** How long a pause in typing settles before the value is committed, in ms. */
  readonly commitDelay?: number;
};

/**
 * A client leaf: it holds what is being typed and the timer that settles it.
 * The committed value is the caller's state, so the parts around it stay
 * server-rendered and the facet has no client root.
 *
 * Why the box holds the typing state rather than the caller: what is in the
 * box mid-word is not yet a value — it can be empty, a lone minus sign, or
 * out of bounds — and handing that upward would put every caller in the
 * business of telling a half-typed number from a committed one. The caller
 * only ever sees strings it is meant to parse.
 *
 * The timer is a plain ref rather than a debounce hook: the hook available
 * here rebuilds its debounced instance whenever the callback's identity
 * changes, which silently drops a pending commit for any caller that does not
 * memoize — and the callback here is a closure over the other end of the
 * range, so it changes whenever that end moves.
 */
export const FilterRangeInput = ({
  className,
  value,
  onValueCommit,
  commitDelay = 500,
  onBlur,
  ...props
}: FilterRangeInputProps) => {
  const [typedValue, setTypedValue] = useState(value);
  const [isTyping, setIsTyping] = useState(false);
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The invariant: a box nobody is typing in shows the committed value. That
  // covers both ways it can fall out of step — the value moving on its own
  // while the other end is dragged, and a pause settling into a commit the
  // caller rounded, clamped or refused. What it must never do is overwrite
  // someone mid-word, which is what the typing flag holds off.
  //
  // Adjusted during render rather than in an effect: an effect would paint the
  // stale text first and correct it on a second pass, which is visible as a
  // flicker on a dragged thumb.
  if (!isTyping && typedValue !== value) {
    setTypedValue(value);
  }

  const cancelPendingCommit = () => {
    if (commitTimer.current !== null) {
      clearTimeout(commitTimer.current);
      commitTimer.current = null;
    }
  };

  useEffect(() => () => cancelPendingCommit(), []);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const typed = event.target.value;

    setTypedValue(typed);
    setIsTyping(true);
    cancelPendingCommit();

    commitTimer.current = setTimeout(() => {
      setIsTyping(false);
      onValueCommit(typed);
    }, commitDelay);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    cancelPendingCommit();
    // Clearing the flag is what puts the box back under the invariant above:
    // whatever the caller makes of this string — clamped, rounded, refused —
    // the box shows the committed value from the next render on.
    setIsTyping(false);
    onValueCommit(typedValue);

    onBlur?.(event);
  };

  return (
    <input
      type="number"
      data-slot="filter-range-input"
      className={clsxm(filterRangeInputClasses, className)}
      value={typedValue}
      onChange={handleChange}
      onBlur={handleBlur}
      {...props}
    />
  );
};
